import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import InvestmentTiers from './components/InvestmentTiers';
import ROICalculator from './components/ROICalculator';
import PortfolioDashboard from './components/PortfolioDashboard';
import PayoutTracking from './components/PayoutTracking';
import KYCApplicationForm from './components/KYCApplicationForm';
import { TrendingUp, BarChart2, Briefcase, CreditCard, FileText, ArrowLeft, Menu, X } from 'lucide-react';

const NAV_ITEMS = [
  { id: 'tiers', label: 'Investment Tiers', icon: TrendingUp },
  { id: 'calculator', label: 'ROI Calculator', icon: BarChart2 },
  { id: 'portfolio', label: 'My Portfolio', icon: Briefcase },
  { id: 'payouts', label: 'Payout Tracking', icon: CreditCard },
  { id: 'apply', label: 'Apply Now', icon: FileText },
];

const FleetFinanceScreen = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('tiers');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [investments, setInvestments] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      if (user) {
        // Fetch investor profile for this user
        const { data: profile } = await supabase
          ?.from('investor_profiles')
          ?.select('id')
          ?.eq('user_id', user?.id)
          ?.maybeSingle();

        if (profile?.id) {
          // Fetch investments
          const { data: invData } = await supabase
            ?.from('investments')
            ?.select('*')
            ?.eq('investor_id', profile?.id)
            ?.order('created_at', { ascending: false });

          setInvestments(invData || []);

          // Fetch vehicles for all investments
          if (invData?.length > 0) {
            const investmentIds = invData?.map((i) => i?.id);
            const { data: vData } = await supabase
              ?.from('investment_vehicles')
              ?.select('*')
              ?.in('investment_id', investmentIds);
            setVehicles(vData || []);
          }

          // Fetch payouts
          const { data: pData } = await supabase
            ?.from('investment_payouts')
            ?.select('*')
            ?.eq('investor_id', profile?.id)
            ?.order('payout_date', { ascending: false });
          setPayouts(pData || []);
        } else {
          // Load demo data for unauthenticated/no-profile users
          await loadDemoData();
        }
      } else {
        await loadDemoData();
      }
    } catch (err) {
      console.error('Fleet finance data fetch error:', err?.message);
      await loadDemoData();
    } finally {
      setLoading(false);
    }
  };

  const loadDemoData = async () => {
    try {
      const { data: invData } = await supabase
        ?.from('investments')
        ?.select('*')
        ?.limit(5);
      setInvestments(invData || []);

      if (invData?.length > 0) {
        const ids = invData?.map((i) => i?.id);
        const { data: vData } = await supabase
          ?.from('investment_vehicles')
          ?.select('*')
          ?.in('investment_id', ids);
        setVehicles(vData || []);
      }

      const { data: pData } = await supabase
        ?.from('investment_payouts')
        ?.select('*')
        ?.order('payout_date', { ascending: false })
        ?.limit(20);
      setPayouts(pData || []);
    } catch (err) {
      console.error('Demo data load error:', err?.message);
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
    const el = document.getElementById(sectionId);
    if (el) {
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSelectTier = (tier) => {
    setSelectedTier(tier);
    scrollToSection('apply');
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Top Header */}
      <header className="bg-[#1a2744] sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="text-white/60 hover:text-white transition-colors flex items-center gap-1.5 text-sm"
              >
                <ArrowLeft size={16} />
                <span className="hidden sm:block">Back</span>
              </button>
              <div className="h-5 w-px bg-white/20" />
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[#c9a84c] rounded-lg flex items-center justify-center">
                  <TrendingUp size={14} className="text-[#1a2744]" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm leading-tight">OnTheGo Fleet Finance</div>
                  <div className="text-white/40 text-xs">Investment Platform</div>
                </div>
              </div>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => scrollToSection(item?.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    activeSection === item?.id
                      ? 'bg-[#c9a84c] text-[#1a2744]'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <item.icon size={13} />
                  {item?.label}
                </button>
              ))}
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white p-2"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#0f1a35] border-t border-white/10 px-4 py-3">
            {NAV_ITEMS?.map((item) => (
              <button
                key={item?.id}
                onClick={() => scrollToSection(item?.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all mb-1 ${
                  activeSection === item?.id
                    ? 'bg-[#c9a84c] text-[#1a2744]'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.icon size={15} />
                {item?.label}
              </button>
            ))}
          </div>
        )}
      </header>
      {/* Hero Banner */}
      <div className="bg-gradient-to-br from-[#1a2744] via-[#1e2f52] to-[#0f1a35] py-14 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#c9a84c]/20 text-[#c9a84c] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <TrendingUp size={12} />
              Fleet Investment Platform
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
              Invest in Kenya's<br />
              <span className="text-[#c9a84c]">Fastest-Growing</span> Mobility Fleet
            </h1>
            <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-lg">
              Earn up to 18% annual returns by financing OnTheGo's fleet of motorcycles and tuk-tuks. Transparent payouts, real-time tracking, and full KYC compliance.
            </p>
            <div className="flex flex-wrap gap-6">
              {[
                { label: 'Annual ROI', value: 'Up to 18%' },
                { label: 'Min. Investment', value: 'KES 5,000' },
                { label: 'Payout Cycle', value: 'Monthly' },
              ]?.map((stat) => (
                <div key={stat?.label}>
                  <div className="text-[#c9a84c] font-bold text-xl">{stat?.value}</div>
                  <div className="text-white/40 text-xs">{stat?.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="space-y-16">
            {/* Investment Tiers Skeleton */}
            <section>
              <div className="mb-8 space-y-2">
                <div className="w-48 h-7 rounded-lg bg-slate-200 animate-pulse" />
                <div className="w-72 h-4 rounded bg-slate-200 animate-pulse" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[...Array(3)]?.map((_, i) => (
                  <div key={i} className="rounded-2xl bg-white border border-slate-200 p-6 space-y-4" style={{ animationDelay: `${i * 80}ms` }}>
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 rounded-xl bg-slate-200 animate-pulse" />
                      <div className="w-16 h-6 rounded-full bg-slate-200 animate-pulse" />
                    </div>
                    <div className="space-y-2">
                      <div className="w-28 h-6 rounded bg-slate-200 animate-pulse" />
                      <div className="w-full h-4 rounded bg-slate-200 animate-pulse" />
                      <div className="w-3/4 h-4 rounded bg-slate-200 animate-pulse" />
                    </div>
                    <div className="space-y-2 pt-2">
                      {[...Array(4)]?.map((_, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-slate-200 animate-pulse flex-shrink-0" />
                          <div className="flex-1 h-3 rounded bg-slate-200 animate-pulse" />
                        </div>
                      ))}
                    </div>
                    <div className="w-full h-11 rounded-xl bg-slate-200 animate-pulse mt-2" />
                  </div>
                ))}
              </div>
            </section>
            {/* ROI Calculator Skeleton */}
            <section>
              <div className="mb-6 space-y-2">
                <div className="w-40 h-7 rounded-lg bg-slate-200 animate-pulse" />
                <div className="w-64 h-4 rounded bg-slate-200 animate-pulse" />
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-5">
                    {[...Array(3)]?.map((_, i) => (
                      <div key={i} className="space-y-2">
                        <div className="w-32 h-4 rounded bg-slate-200 animate-pulse" />
                        <div className="w-full h-10 rounded-xl bg-slate-200 animate-pulse" />
                      </div>
                    ))}
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-5 space-y-4">
                    <div className="w-36 h-5 rounded bg-slate-200 animate-pulse" />
                    {[...Array(4)]?.map((_, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="w-28 h-4 rounded bg-slate-200 animate-pulse" />
                        <div className="w-20 h-5 rounded bg-slate-200 animate-pulse" />
                      </div>
                    ))}
                    <div className="w-full h-px bg-slate-200" />
                    <div className="flex items-center justify-between">
                      <div className="w-24 h-5 rounded bg-slate-200 animate-pulse" />
                      <div className="w-28 h-7 rounded bg-slate-200 animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
            </section>
            {/* Portfolio Stats Skeleton */}
            <section>
              <div className="mb-6 space-y-2">
                <div className="w-36 h-7 rounded-lg bg-slate-200 animate-pulse" />
                <div className="w-56 h-4 rounded bg-slate-200 animate-pulse" />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[...Array(4)]?.map((_, i) => (
                  <div key={i} className="h-28 rounded-2xl bg-slate-200 animate-pulse" style={{ animationDelay: `${i * 60}ms` }} />
                ))}
              </div>
              {/* Table Skeleton */}
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="p-5 border-b border-slate-100">
                  <div className="w-40 h-5 rounded bg-slate-200 animate-pulse" />
                </div>
                <div className="divide-y divide-slate-100">
                  {[...Array(4)]?.map((_, i) => (
                    <div key={i} className="flex items-center gap-4 px-5 py-4" style={{ animationDelay: `${i * 60}ms` }}>
                      <div className="w-10 h-10 rounded-xl bg-slate-200 animate-pulse flex-shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <div className="w-32 h-4 rounded bg-slate-200 animate-pulse" />
                        <div className="w-24 h-3 rounded bg-slate-200 animate-pulse" />
                      </div>
                      <div className="w-20 h-6 rounded-full bg-slate-200 animate-pulse" />
                      <div className="w-24 h-5 rounded bg-slate-200 animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            </section>
            {/* Payout Tracking Skeleton */}
            <section>
              <div className="mb-6 space-y-2">
                <div className="w-40 h-7 rounded-lg bg-slate-200 animate-pulse" />
                <div className="w-60 h-4 rounded bg-slate-200 animate-pulse" />
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                  <div className="w-32 h-5 rounded bg-slate-200 animate-pulse" />
                  <div className="w-24 h-8 rounded-lg bg-slate-200 animate-pulse" />
                </div>
                <div className="divide-y divide-slate-100">
                  {[...Array(5)]?.map((_, i) => (
                    <div key={i} className="flex items-center gap-4 px-5 py-4" style={{ animationDelay: `${i * 60}ms` }}>
                      <div className="flex-1 space-y-1.5">
                        <div className="w-28 h-4 rounded bg-slate-200 animate-pulse" />
                        <div className="w-20 h-3 rounded bg-slate-200 animate-pulse" />
                      </div>
                      <div className="w-20 h-6 rounded-full bg-slate-200 animate-pulse" />
                      <div className="w-24 h-5 rounded bg-slate-200 animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        ) : (
          <>
            <InvestmentTiers onSelectTier={handleSelectTier} />
            <ROICalculator />
            <PortfolioDashboard investments={investments} vehicles={vehicles} payouts={payouts} loading={loading} />
            <PayoutTracking payouts={payouts} loading={loading} />
            <KYCApplicationForm selectedTier={selectedTier} onSuccess={fetchData} />
          </>
        )}
      </main>
      {/* Footer */}
      <footer className="bg-[#1a2744] py-8 px-4 mt-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-white/40 text-xs">
            © 2026 OnTheGo Fleet Finance. All investments carry risk. Past performance does not guarantee future returns.
          </div>
          <div className="flex items-center gap-4 text-xs text-white/40">
            <Link to="/" className="hover:text-white/70 transition-colors">Home</Link>
            <Link to="/admin-dashboard" className="hover:text-white/70 transition-colors">Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FleetFinanceScreen;
